import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPortfolioBalanceHistory, Granularity } from '../../api/metrics';
import {XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart} from 'recharts';
import StandaloneSection from '../shared/StandaloneSection';
import './BalanceHistoryChart.css';


type TimeRange = '24h' | 'today' | '7d' | '30d' | '6mo' | '1yr' | 'ytd' | 'all';
type BalanceMetric = 'total' | 'crypto' | 'cash';

interface ChartDataPoint {
  timestamp: number;
  balance?: number;
  date: string;
}

interface TimeRangeConfig {
  hours: number;
  granularity: Granularity;
  label: string;
  // isCalendarBased: indicates whether the time range is calculated from a calendar boundary (e.g., start of day/year)
  // rather than a rolling window (e.g., "last 24 hours"). Calendar-based ranges like "Today" and "YTD"
  // start from midnight UTC or Jan 1 UTC, while rolling ranges like "24h" or "1D" look back from the current moment.
  isCalendarBased: boolean;
}

/**
 * Balance History Chart Component
 * Displays historical portfolio balance trends with time range selectors
 * Uses different data granularities based on time range to optimize performance:
 * - 7d: 5-minute intervals
 * - 30d, 6mo, 1yr, ytd: 1-day intervals
 * - all: 1-month intervals
 */
const BalanceHistoryChart: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState<TimeRange>('24h');
  const [selectedMetric, setSelectedMetric] = useState<BalanceMetric>('total');

  const getTimeRangeConfig = useMemo((): TimeRangeConfig => {
    const now = new Date();

    switch (selectedRange) {
      case '24h':
        return { hours: 24, granularity: Granularity.RAW, label: 'Last 24 Hours', isCalendarBased: false };
      case 'today': {
        // Calendar-based: from midnight today (UTC) to now
        // Use UTC midnight to match database timezone
        const startOfTodayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        const hoursSinceMidnight = Math.floor((now.getTime() - startOfTodayUTC.getTime()) / (1000 * 60 * 60));
        // Ensure at least 1 hour to avoid empty results, or use RAW granularity for sub-hour data
        const hours = Math.max(1, hoursSinceMidnight);
        return { hours, granularity: Granularity.RAW, label: 'Today (UTC)', isCalendarBased: true };
      }
      case '7d':
        return { hours: 7 * 24, granularity: Granularity.FIVE_MIN, label: '7 Days', isCalendarBased: false };
      case '30d':
        return { hours: 30 * 24, granularity: Granularity.HOUR, label: '30 Days', isCalendarBased: false };
      case '6mo':
        return { hours: 180 * 24, granularity: Granularity.DAY, label: '6 Months', isCalendarBased: false };
      case '1yr':
        return { hours: 365 * 24, granularity: Granularity.DAY, label: '1 Year', isCalendarBased: false };
      case 'ytd': {
        // Calendar-based: from Jan 1 midnight (UTC) to now
        // Use UTC to match database timezone
        const startOfYearUTC = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
        const hoursSinceYearStart = Math.floor((now.getTime() - startOfYearUTC.getTime()) / (1000 * 60 * 60));
        return { hours: hoursSinceYearStart, granularity: Granularity.DAY, label: 'YTD (UTC)', isCalendarBased: true };
      }
      case 'all':
        // All time: ~10 years of data at monthly granularity
        return { hours: 87600, granularity: Granularity.MONTH, label: 'All Time', isCalendarBased: false };
      default:
        return { hours: 7 * 24, granularity: Granularity.FIVE_MIN, label: '7 Days', isCalendarBased: false };
    }
  }, [selectedRange]);

  // Fetch historical balance data
  const { data, isLoading, isError } = useQuery({
    queryKey: ['portfolio', 'balance', 'history', selectedRange, selectedMetric, getTimeRangeConfig.hours, getTimeRangeConfig.granularity],
    queryFn: () => getPortfolioBalanceHistory(getTimeRangeConfig.hours, getTimeRangeConfig.granularity),
    retry: 2,
  });

  // Get metric label for display
  const getMetricLabel = () => {
    switch (selectedMetric) {
      case 'total':
        return 'Overall Balance';
      case 'crypto':
        return 'Crypto Balance';
      case 'cash':
        return 'Cash Balance';
      default:
        return 'Balance';
    }
  };

  // Transform API data to chart format based on selected metric
  const chartData: ChartDataPoint[] = (data || []).map((snapshot: any) => {
    let balanceValue: number;
    switch (selectedMetric) {
      case 'crypto':
        balanceValue = parseFloat(snapshot.totalCryptoBalanceValue);
        break;
      case 'cash':
        balanceValue = parseFloat(snapshot.totalCashEquivalentBalanceValue);
        break;
      case 'total':
      default:
        balanceValue = parseFloat(snapshot.totalBalanceValue);
        break;
    }
    
    return {
      timestamp: snapshot.snapshotTime,
      balance: balanceValue === 0 ? undefined : balanceValue,
      date: new Date(snapshot.snapshotTime).toISOString()
    };
  });

  // Sort by timestamp ascending
  chartData.sort((a, b) => a.timestamp - b.timestamp);

  // Get granularity description for display
  const getGranularityLabel = () => {
    switch (getTimeRangeConfig.granularity) {
      case Granularity.RAW:
        return 'Showing raw balance data';
      case Granularity.FIVE_MIN:
        return 'Showing 5-minute interval balances';
      case Granularity.HOUR:
        return 'Showing hourly balances';
      case Granularity.DAY:
        return 'Showing daily balances (end of day)';
      case Granularity.MONTH:
        return 'Showing monthly balances';
      default:
        return 'Showing balance data';
    }
  };

  // Calculate balance trend (positive, negative, or flat)
  const getTrendColor = () => {
    if (chartData.length < 2) return '#8a8ced'; // Default purple if insufficient data

    const firstBalance = chartData[0].balance;
    const lastBalance = chartData[chartData.length - 1].balance;

    if(firstBalance === undefined || lastBalance === undefined) return '#8a8ced';

    const change = lastBalance - firstBalance;
    const percentChange = (change / firstBalance) * 100;

    if (percentChange > 0.5) return '#10b981'; // Green for positive trend
    if (percentChange < -0.5) return '#ef4444'; // Red for negative trend
    return '#8a8ced'; // Purple for flat/minimal change
  };

  const trendColor = getTrendColor();

  // Format currency for display
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format date for X-axis based on granularity (using UTC to match database timezone)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    switch (getTimeRangeConfig.granularity) {
      case Granularity.RAW:
      case Granularity.FIVE_MIN:
        // For 5-minute data, show day and half-day (AM/PM)
        const hours = date.getUTCHours();
        const dayOfWeek = date.toLocaleDateString('en-US', {
          weekday: 'short',
          timeZone: 'UTC'
        });
        const period = hours < 12 ? 'AM' : 'PM';
        const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${dayOfWeek} ${displayHour}${period}`;
      case Granularity.HOUR:
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
          hour12: true,
          timeZone: 'UTC'
        });
      case Granularity.DAY:
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC'
        });
      case Granularity.MONTH:
        return date.toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit',
          timeZone: 'UTC'
        });
      default:
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC'
        });
    }
  };

  // Custom tooltip for hover
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const dateFormatOptions =
          getTimeRangeConfig.granularity === Granularity.FIVE_MIN || getTimeRangeConfig.granularity === Granularity.RAW ? {
            weekday: 'short' as const,
            month: 'short' as const,
            day: 'numeric' as const,
            year: 'numeric' as const,
            hour: 'numeric' as const,
            minute: 'numeric' as const,
            hour12: true,
            timeZone: 'UTC' as const
          } :
          getTimeRangeConfig.granularity === Granularity.HOUR ? {
            weekday: 'short' as const,
            month: 'short' as const,
            day: 'numeric' as const,
            year: 'numeric' as const,
            hour: 'numeric' as const,
            hour12: true,
            timeZone: 'UTC' as const
          } :
          getTimeRangeConfig.granularity === Granularity.MONTH ? {
            month: 'short' as const,
            year: 'numeric' as const,
            timeZone: 'UTC' as const
          } :
          {
              weekday: 'short' as const,
              month: 'short' as const,
              day: 'numeric' as const,
              year: 'numeric' as const,
              timeZone: 'UTC' as const
          };

      return (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1e293b' }}>
            {new Date(data.date).toLocaleDateString('en-US', dateFormatOptions)}
          </p>
          <p style={{
            color: '#1e293b',
            fontWeight: 600,
            fontSize: '0.95rem'
          }}>
            {getMetricLabel()}: {formatCurrency(data.balance)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate Y-axis domain with padding
  const getYAxisDomain = () => {
    if (chartData.length === 0) return [0, 100000];

    const balances = chartData.map(d => d.balance).flatMap(b => b !== undefined ? [b] : []);
    const minBalance = Math.min(...balances);
    const maxBalance = Math.max(...balances);
    const range = maxBalance - minBalance;
    const padding = range * 0.1;

    return [
      Math.max(0, minBalance - padding),
      maxBalance + padding
    ];
  };

  const [yMin, yMax] = getYAxisDomain();

  if (isLoading) {
    return (
      <StandaloneSection title="BALANCE HISTORY">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading balance history...</p>
        </div>
      </StandaloneSection>
    );
  }

  if (isError) {
    return (
      <StandaloneSection title="BALANCE HISTORY">
        <div className="error-message">
          <p>Unable to load balance history</p>
        </div>
      </StandaloneSection>
    );
  }

  return (
    <StandaloneSection title="BALANCE HISTORY">
      <div className="balance-history-container">
        {/* Time Range Buttons - Primary Control */}
        <div className="time-range-selector">
          <button
            className={`range-button ${selectedRange === '24h' ? 'active' : ''}`}
            onClick={() => setSelectedRange('24h')}
          >
            Last 24H
          </button>
          <button
            className={`range-button ${selectedRange === 'today' ? 'active' : ''}`}
            onClick={() => setSelectedRange('today')}
          >
            Today
          </button>
          <button
            className={`range-button ${selectedRange === '7d' ? 'active' : ''}`}
            onClick={() => setSelectedRange('7d')}
          >
            7D
          </button>
          <button
            className={`range-button ${selectedRange === '30d' ? 'active' : ''}`}
            onClick={() => setSelectedRange('30d')}
          >
            30D
          </button>
          <button
            className={`range-button ${selectedRange === '6mo' ? 'active' : ''}`}
            onClick={() => setSelectedRange('6mo')}
          >
            6M
          </button>
          <button
            className={`range-button ${selectedRange === '1yr' ? 'active' : ''}`}
            onClick={() => setSelectedRange('1yr')}
          >
            1Y
          </button>
          <button
            className={`range-button ${selectedRange === 'ytd' ? 'active' : ''}`}
            onClick={() => setSelectedRange('ytd')}
          >
            YTD
          </button>
          <button
            className={`range-button ${selectedRange === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedRange('all')}
          >
            ALL
          </button>
        </div>

        {/* Metric Tabs Container */}
        <div className="metric-tabs-container">
          {/* Tab Headers */}
          <div className="metric-tab-headers">
            <button
              className={`metric-tab-header ${selectedMetric === 'total' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('total')}
            >
              Overall Balance
            </button>
            <button
              className={`metric-tab-header ${selectedMetric === 'crypto' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('crypto')}
            >
              Crypto Balance
            </button>
            <button
              className={`metric-tab-header ${selectedMetric === 'cash' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('cash')}
            >
              Cash Balance
            </button>
          </div>

          {/* Tab Content - Chart */}
          <div className="metric-tab-content">
            {chartData.length > 0 ? (
              <>
                  {/* Period Change Summary */}
                  {chartData.length >= 2 && (() => {
                      const startBalance = chartData[0].balance;
                      const endBalance = chartData[chartData.length - 1].balance;
                        if (startBalance === undefined || endBalance === undefined) return <div/>;
                      const absoluteChange = endBalance - startBalance;
                      const percentChange = (absoluteChange / startBalance) * 100;
                      const isPositive = absoluteChange >= 0;

                      return (
                          <div className="period-change-summary">
                              <span className="period-label">Period Change:</span>
                              <span className={`change-amount ${isPositive ? 'positive' : 'negative'}`}>
                        {isPositive ? '+' : ''}{formatCurrency(absoluteChange)}
                      </span>
                              <span className={`change-percent ${isPositive ? 'positive' : 'negative'}`}>
                        ({isPositive ? '+' : ''}{percentChange.toFixed(2)}%)
                      </span>
                          </div>
                      );
                  })()}
                <div className="chart-wrapper">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                      <defs>
                        <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={trendColor} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={trendColor} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDate}
                        tick={{ fontSize: 12 }}
                        minTickGap={30}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tickFormatter={formatCurrency}
                        tick={{ fontSize: 12 }}
                        domain={[yMin, yMax]}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="balance"
                        stroke={trendColor}
                        strokeWidth={2}
                        fill="url(#balanceGradient)"
                        connectNulls={false}  // Explicitly show gaps for missing data
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Granularity Label */}
                <div className="granularity-label">
                  {getGranularityLabel()}
                </div>


              </>
            ) : (
              <div className="no-data">No balance history available for this time range</div>
            )}
          </div>
        </div>
      </div>
    </StandaloneSection>
  );
};

export default BalanceHistoryChart;

